/**
 * Who lives in the valley, how each of them moves, and where their art is.
 *
 * Ported from `atlas` and `idles` in apps/display/public/wildlife.js. The
 * BEHAVIOUR numbers -- gait, speed, rest, height -- are the display's, tuned
 * by eye against the paintings, and are copied rather than reconsidered.
 *
 * WHAT CHANGED is where the art is looked up. The display hard-codes each
 * species' sheet FILE and ROW. Here a species names a sheet by its stable
 * `id` from tools/assets.toml, and its row is found by looking the species up
 * in that sheet's `names`. So a sheet that is repainted -- even one whose rows
 * come back in a different order -- is a manifest edit and nothing else.
 *
 * `resolve` checks every one of those lookups when this module loads. A
 * species missing from its sheet throws immediately, in the test run, rather
 * than drawing the wrong animal on a family's screen.
 */

import { SHEETS, type SheetId } from '../art.generated';

export const SPECIES = [
  'deer', 'fox', 'raccoon', 'squirrel', 'duck', 'frog', 'butterfly',
  'rabbit', 'bird', 'owl', 'bat', 'hedgehog', 'moth',
] as const;
export type Species = (typeof SPECIES)[number];

/** Legacy travel-sheet capacity; each rig declares its actual sequence length. */
export const FRAMES = 12;

/** Repeat the clean up/down stroke; omit off-model export poses and duplicate extremes. */
export const TRAVEL_ORDER: Partial<Record<Species, readonly number[]>> = {
  // The export contains a second launch in its middle; retain one complete hop.
  frog: [0, 0, 1, 2, 3, 3, 3, 5, 8, 9, 0, 0],
  rabbit: [0, 0, 1, 2, 3, 4, 5, 6, 7, 8, 0, 0],
  bird: [0, 1, 2, 3, 4, 3, 2, 1],
  owl: [0, 1, 2, 3, 4, 5, 6, 5, 4, 3, 2, 1],
  // A full wingbeat must include the low/downstroke poses, not reverse at horizontal.
  bat: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  moth: [0, 1, 2, 3, 4, 5, 6, 5, 4, 3, 2, 1],
  butterfly: [1, 2, 3, 4, 5, 6, 5, 4, 3, 2],
};

interface Behaviour {
  /** The sheet with this species' walk, hop or flight cycle. */
  sheet: SheetId;
  /** The sheet with its five paired idle poses. */
  idleSheet: SheetId;
  /** Deer only: the row a grazing deer is drawn from. */
  grazeName?: string;
  grazeSheet?: SheetId;
  turnSheet?: SheetId;
  /** Native direction of travel artwork; the entire drawing is mirrored at render. */
  walkFacing?: 1 | -1;
  idleFacings?: readonly (1 | -1)[];
  /** How much of a cell the animal fills; sizes it by body, not padding. */
  fill: number;
  /** Seconds per stride, hop or wingbeat. */
  gait: number;
  /** Painted height in pixels at the reference 941px-tall screen. */
  height: number;
  /** Pixels per second across the reference screen. */
  speed: number;
  /** Seconds spent resting at each end of a route. */
  rest: number;
  climbs?: boolean;
  hops?: boolean;
  flying?: boolean;
  nocturnal?: boolean;
}

const BEHAVIOUR: Record<Species, Behaviour> = {
  deer:      { sheet: 'travel-deer', walkFacing: -1, idleFacings: [-1, 1], idleSheet: 'idle-deer', turnSheet: 'turn-deer', fill: 0.80, gait: 1.05, height: 142, speed: 34, rest: 7 },
  fox:       { sheet: 'woodland-actions', idleSheet: 'idle-fox', turnSheet: 'turn-fox', fill: 0.54, gait: 0.48, height: 80, speed: 94, rest: 2.6 },
  raccoon:   { sheet: 'travel-raccoon', idleSheet: 'idle-raccoon', turnSheet: 'turn-raccoon', fill: 0.48, gait: 0.68, height: 65, speed: 78, rest: 3.2 },
  squirrel:  { sheet: 'travel-squirrel', idleSheet: 'idle-squirrel', turnSheet: 'turn-squirrel', fill: 0.74, gait: 0.72, height: 59, speed: 66, rest: 1.8, climbs: true },
  duck:      { sheet: 'habitat-actions', idleSheet: 'idle-duck', turnSheet: 'turn-duck', fill: 0.43, gait: 1.1, height: 48, speed: 19, rest: 0.6 },
  frog:      { sheet: 'habitat-actions', idleSheet: 'idle-frog', turnSheet: 'turn-frog', fill: 0.46, gait: 1.45, height: 52, speed: 23, rest: 3.6, hops: true },
  butterfly: { sheet: 'habitat-actions', idleSheet: 'idle-butterfly', turnSheet: 'turn-butterfly', fill: 0.58, gait: 0.33, height: 31, speed: 26, rest: 1.4, flying: true },
  rabbit:    { sheet: 'meadow-flight', idleSheet: 'idle-rabbit', turnSheet: 'turn-rabbit', fill: 0.68, gait: 0.95, height: 69, speed: 52, rest: 3.5, hops: true },
  bird:      { sheet: 'meadow-flight', idleFacings: [-1,1,-1,-1,-1,1,-1,1,-1,1], idleSheet: 'idle-bird', turnSheet: 'turn-bird', fill: 0.58, gait: 0.6, height: 31, speed: 88, rest: 4, flying: true },
  owl:       { sheet: 'night-moves', idleSheet: 'idle-owl', turnSheet: 'turn-owl', fill: 0.70, gait: 0.9, height: 70, speed: 75, rest: 6, flying: true, nocturnal: true },
  bat:       { sheet: 'night-moves', idleSheet: 'idle-bat', turnSheet: 'turn-bat', fill: 0.62, gait: 0.32, height: 42, speed: 103, rest: 5, flying: true, nocturnal: true },
  hedgehog:  { sheet: 'travel-hedgehog', idleSheet: 'idle-hedgehog', turnSheet: 'turn-hedgehog', fill: 0.45, gait: 0.85, height: 47, speed: 32, rest: 5, nocturnal: true },
  moth:      { sheet: 'night-moves', idleSheet: 'idle-moth', turnSheet: 'turn-moth', fill: 0.60, gait: 0.42, height: 36, speed: 23, rest: 4, flying: true, nocturnal: true },
};

/**
 * The five idle actions for each species, in the order they are painted
 * across its idle row -- two frames each, so action `n` is frames 2n and 2n+1.
 */
export const IDLES: Record<Species, readonly string[]> = {
  deer: ['graze', 'listen', 'groom', 'stretch', 'doze'],
  fox: ['tail-flick', 'sniff', 'scratch', 'stretch', 'doze'],
  raccoon: ['paw-rub', 'sniff', 'groom', 'look-around', 'doze'],
  rabbit: ['nose-twitch', 'groom', 'nibble', 'ear-twitch', 'doze'],
  squirrel: ['nibble', 'groom', 'listen', 'stretch', 'doze'],
  duck: ['look-around', 'preen', 'dabble', 'stretch', 'doze'],
  frog: ['throat-puff', 'blink', 'listen', 'stretch', 'doze'],
  butterfly: ['wing-fan', 'wing-fold', 'groom', 'sway', 'doze'],
  bird: ['look-around', 'preen', 'stretch', 'fluff', 'doze'],
  owl: ['blink', 'look-around', 'preen', 'stretch', 'doze'],
  bat: ['blink', 'wing-fold', 'groom', 'look-around', 'doze'],
  hedgehog: ['sniff', 'stretch', 'groom', 'look-around', 'doze'],
  moth: ['wing-fan', 'wing-fold', 'groom', 'stretch', 'doze'],
};

/** Where one pose lives: which sheet, and which row and grid within it. */
export interface Cells {
  sheet: SheetId;
  row: number;
  rows: number;
  columns: number;
  /** A sequence can continue onto the following row for wider silhouettes. */
  sequenceRows: number;
  /** Used poses, allowing spare export cells to be excluded from playback. */
  frames: number;
}

export interface Rig extends Omit<Behaviour, 'sheet' | 'idleSheet' | 'grazeName' | 'grazeSheet' | 'turnSheet'> {
  name: Species;
  walk: Cells;
  idle: Cells;
  /** Eight headings; flyers have four independent wingbeat rows per heading. */
  turn: Cells;
  /** Deer only. */
  graze?: Cells;
}

function cells(sheet: SheetId, name: string, species: Species): Cells {
  const info = SHEETS[sheet];
  const row = (info.names as readonly string[]).indexOf(name);
  if (row < 0) {
    throw new Error(
      `${species} expects a "${name}" row on sheet "${sheet}", which has ` +
        `[${info.names.join(', ')}]. Fix tools/assets.toml or this atlas.`,
    );
  }
  return { sheet, row, rows: info.rows, columns: info.columns, sequenceRows: info.sequenceRows,
    frames: (info.frameCounts as Readonly<Record<string, number>>)[name] ?? info.columns * info.sequenceRows };
}

function resolve(): Record<Species, Rig> {
  const out = {} as Record<Species, Rig>;
  for (const name of SPECIES) {
    const { sheet, idleSheet, grazeName, grazeSheet, turnSheet, ...rest } = BEHAVIOUR[name];
    out[name] = {
      ...rest,
      name,
      walk: cells(sheet, name, name),
      idle: cells(idleSheet, name, name),
      turn: cells(
        turnSheet ?? (name === 'squirrel' ? 'turn-squirrel'
          : sheet === 'woodland-actions' ? 'turn-woodland'
          : sheet === 'habitat-actions' ? 'turn-habitat'
          : sheet === 'meadow-flight' ? 'turn-meadow' : 'turn-night'),
        name, name,
      ),
      ...(grazeName ? { graze: cells(grazeSheet ?? sheet, grazeName, name) } : {}),
    };
    if (IDLES[name].length * 2 > out[name].idle.frames) {
      throw new Error(`${name} has ${IDLES[name].length} idles, which need more columns than ${idleSheet} has.`);
    }
    if (out[name].walk.frames < 8 || out[name].walk.frames > out[name].walk.columns * out[name].walk.sequenceRows
      || out[name].turn.columns * out[name].turn.sequenceRows !== (rest.flying ? 32 : 8)) {
      throw new Error(`${name} needs eight turn headings and flyers need four wingbeat phases.`);
    }
  }
  return out;
}

export const ATLAS: Record<Species, Rig> = resolve();

/** Native direction applies to the whole painting, including custom idle cells. */
export function nativeFacing(name: Species, cells: Cells, frame: number): number {
  const rig = ATLAS[name];
  if (cells.sheet === rig.walk.sheet) return rig.walkFacing ?? 1;
  if (cells.sheet === rig.idle.sheet) {
    return rig.idleFacings?.[(cells.row - rig.idle.row) * cells.columns + frame] ?? 1;
  }
  return 1;
}
